/**
 * Practitioner Dashboard Screen (Container Component)
 *
 * Upgraded from the group-management view to a full analytics dashboard.
 * Group management is accessible as a sub-route (practitioner-manage/groups).
 *
 * Architecture (Option A from SPEC.md §4.2):
 * - Keeps existing practitioner-manage.tsx route; upgrades UI in-place.
 * - GlassBackground as root wrapper (handles reduced-transparency, Android, web).
 * - PractitionerHeader sticky at top (z:50).
 * - ScrollView with 2×2 stat grid, QuickActionsRow, RecentAnalysesFeed.
 *
 * Data: usePractitionerDashboard() — mock data until DB schema migration runs.
 *
 * Analytics events per SPEC.md §4.4.
 */

import React, { useCallback, useMemo } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Alert,
  Share,
  ViewStyle,
} from 'react-native';
import { Stack } from 'expo-router';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import { GlassBackground } from '../../components/glass/GlassBackground';
import { useTabScreenPadding } from '../../components/layout/TabScreenWrapper';
import { usePractitionerDashboard } from '../../hooks/usePractitionerDashboard';
import { PractitionerHeader } from '../../components/screens/practitioner-dashboard/PractitionerHeader';
import { DashboardGreeting } from '../../components/screens/practitioner-dashboard/DashboardGreeting';
import { DashboardStatCard } from '../../components/screens/practitioner-dashboard/DashboardStatCard';
import { QuickActionsRow } from '../../components/screens/practitioner-dashboard/QuickActionsRow';
import { RecentAnalysesFeed } from '../../components/screens/practitioner-dashboard/RecentAnalysesFeed';

/** Derive time-of-day greeting key from current hour. */
function getGreetingKey(
  hour: number
): 'greeting.good_morning' | 'greeting.good_afternoon' | 'greeting.good_evening' {
  if (hour < 12) return 'greeting.good_morning';
  if (hour < 18) return 'greeting.good_afternoon';
  return 'greeting.good_evening';
}

export default function PractitionerDashboardScreen() {
  const { t, i18n } = useTranslation('practitioner');
  const locale = i18n.language;
  const bottomPadding = useTabScreenPadding();

  const {
    stats,
    recentAnalyses,
    practitionerName,
    practitionerInitials,
    unreadNotificationCount,
    isLoading,
  } = usePractitionerDashboard();

  const greetingKey = useMemo(() => getGreetingKey(new Date().getHours()), []);
  const greeting = t(greetingKey);

  const contentContainerStyle = useMemo(
    () => [styles.scrollContent, { paddingBottom: bottomPadding }],
    [bottomPadding]
  );

  // Track screen view once stats are ready
  React.useEffect(() => {
    if (stats) {
      trackEvent('Screen_View_PractitionerDashboard', {
        total_clients: stats.activeClients,
        analyses_this_month: stats.analysesThisMonth,
      });
    }
  }, [stats]);

  const handleInvitePress = useCallback(() => {
    trackEvent('Button_Click_PractitionerInviteClient', { source: 'dashboard' });
    // TODO: Open invite modal / sheet once implemented.
    Alert.alert(t('actions.invite_client'), t('actions.invite_coming_soon'));
  }, [t]);

  const handleSharePress = useCallback(() => {
    trackEvent('Button_Click_PractitionerShareLink', { source: 'dashboard' });
    Share.share({ message: t('actions.share_message') }).catch(() => {});
  }, [t]);

  const handleViewAll = useCallback(() => {
    trackEvent('Button_Click_PractitionerViewAllAnalyses', {});
    // TODO: Navigate to full analysis list screen.
  }, []);

  const handleAnalysisPress = useCallback((id: string) => {
    trackEvent('Button_Click_PractitionerOpenAnalysis', { analysis_id: id });
    // TODO: Navigate to analysis detail screen.
  }, []);

  const handleNotificationsPress = useCallback(() => {
    // TODO: Open notifications panel.
  }, []);

  const handleAvatarPress = useCallback(() => {
    // TODO: Navigate to practitioner profile screen.
  }, []);

  return (
    <GlassBackground>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={styles.root}>
        {/* Sticky header */}
        <PractitionerHeader
          practitionerName={practitionerName}
          practitionerInitials={practitionerInitials}
          unreadNotificationCount={unreadNotificationCount}
          onNotificationsPress={handleNotificationsPress}
          onAvatarPress={handleAvatarPress}
          t={t}
        />

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={contentContainerStyle}
          showsVerticalScrollIndicator={false}
        >
          {/* Greeting */}
          <DashboardGreeting
            practitionerName={practitionerName}
            greeting={greeting}
          />

          {/* 2×2 stat grid */}
          <View style={styles.statsGrid}>
            <DashboardStatCard
              variant="clients"
              value={stats ? String(stats.activeClients) : '\u2014'}
              label={t('stats.active_clients')}
              delta={
                stats?.activeClientsDelta != null
                  ? `+${stats.activeClientsDelta}`
                  : undefined
              }
              deltaPositive={true}
              accessibilityLabel={t('accessibility.stat_active_clients', {
                count: stats?.activeClients ?? 0,
                delta:
                  stats?.activeClientsDelta != null
                    ? `, +${stats.activeClientsDelta} new`
                    : '',
              })}
              style={styles.statCard}
            />
            <DashboardStatCard
              variant="analyses"
              value={stats ? String(stats.analysesThisMonth) : '\u2014'}
              label={t('stats.analyses_this_month')}
              accessibilityLabel={t('accessibility.stat_analyses', {
                count: stats?.analysesThisMonth ?? 0,
                delta: '',
              })}
              style={styles.statCard}
            />
            <DashboardStatCard
              variant="earnings"
              value={stats ? stats.earningsThisMonth : '\u2014'}
              label={t('stats.earnings_this_month')}
              accessibilityLabel={t('accessibility.stat_earnings', {
                amount: stats?.earningsThisMonth ?? '\u2014',
              })}
              style={styles.statCard}
            />
            <DashboardStatCard
              variant="improving"
              value={stats ? `${stats.clientsImprovingPct}%` : '\u2014'}
              label={t('stats.clients_improving')}
              accessibilityLabel={t('accessibility.stat_improving', {
                percent: stats?.clientsImprovingPct ?? 0,
              })}
              style={styles.statCard}
            />
          </View>

          {/* Quick actions */}
          <QuickActionsRow
            onInvitePress={handleInvitePress}
            onSharePress={handleSharePress}
            t={t}
          />

          {/* Recent analyses */}
          <RecentAnalysesFeed
            analyses={recentAnalyses}
            onViewAll={handleViewAll}
            onAnalysisPress={handleAnalysisPress}
            isLoading={isLoading}
            t={t}
            locale={locale}
          />
        </ScrollView>
      </View>
    </GlassBackground>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  } as ViewStyle,
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  } as ViewStyle,
  statCard: {
    width: '48%',
  } as ViewStyle,
});
